import {
    AdminPreferences,
    SearchInterface,
    KendraConst,
    IMetrics
} from "@amzn/ask-legal-domain";
import { Alert, Pagination, TextContent } from "@amzn/awsui-components-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Builder } from "builder-pattern";
import { AppContext } from "../../setup/context";
import { useLatestRequest } from "../../hooks/use-latest-request";
import { callApi, Loadable } from "../../utils/api-call";
import { SearchResultPanelV2 } from "../../components/search/ResultPanelV2";
import { SmartSearchFilterViewV2 } from "../../components/search/search-components/SmartSearchFilterViewV2";
import { LoadingSpinner } from "../../components/common/loading-spinner";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { UIModel } from "../../model/ui-model";
import { isStandardResult } from "../../utils/search-utils";
import { describeSearchFilters, getSearchFilterKey } from "../../components/search/search-configs/search-filter-utils-v2";
import { SearchChatContext, useSearchChatContext } from "../../components/quick-agent-chat-panel/search-chat-context-store";
import "../../styles/component/layout/SearchResultPageLayout.scss";
import "../../styles/component/layout/PageBaseLayout.scss";
import "../../styles/component/search/automated-answer.scss";

interface GlobalSearchHomeV2Props {
    queryText?: string;
}

const INITIAL_PAGE_SIZE = 10;
const INITIAL_PAGE_NUMBER = 1;

export const GlobalSearchHomeV2: React.FC<GlobalSearchHomeV2Props> = ({ queryText }) => {
    const context = React.useContext(AppContext);
    const [searchState, setSearchState] = React.useState<Loadable<SearchInterface.SearchQueryResponse>>({ status: "idle" });
    const searchOutput = searchState.status === "loaded" ? searchState.data : undefined;
    const searchError = searchState.status === "error" ? searchState.err : undefined;
    // Only the latest search may update state (filters can change while a search is in flight)
    const searchRequest = useLatestRequest();
    const navigate = useNavigate();
    const queryInputState = UIModel.State.use<SearchInterface.SearchQueryInput>({});

    const [authors, setAuthors] = React.useState<string[]>([]);
    const [pageLibs, setPageLibs] = React.useState<string[]>([]);
    const [availableInstances, setAvailableInstances] = React.useState<string[]>([]);
    const [hasLegalOnlyAccess, setHasLegalOnlyAccess] = React.useState(false);

    // Use default search preferences for global search
    const searchPreferences = AdminPreferences.DEFAULT_ADMIN_PREFERENCES.search!;

    const initAuthorOptions = React.useCallback(() => {
        const allAuthors: string[] = [];
        searchOutput.result.ResultItems.forEach(r => {
            const author = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Default.Authors
            );
            if (author?.Value?.StringListValue) {
                allAuthors.push(...author.Value.StringListValue);
            }
        });
        setAuthors([...new Set(allAuthors)]);
    }, [searchOutput]);

    const initPageLibraries = React.useCallback(() => {
        const pageLibs: string[] = [];
        searchOutput.result.ResultItems.forEach(r => {
            const pageLib = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.PageLibraryId
            );
            if (pageLib?.Value?.StringValue) {
                pageLibs.push(pageLib.Value.StringValue);
            }
        });
        setPageLibs([...new Set(pageLibs)]);
    }, [searchOutput]);

    const initAvailableInstances = React.useCallback(() => {
        if (!searchOutput?.result?.FacetResults) return;
        const instanceFacet = searchOutput.result.FacetResults.find(facet =>
            facet.DocumentAttributeKey === KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId
        );
        const instancesFromSearch = instanceFacet?.DocumentAttributeValueCountPairs
            ?.filter(pair => pair.DocumentAttributeValue?.StringValue)
            .map(pair => pair.DocumentAttributeValue.StringValue as string) || [];
        setAvailableInstances(instancesFromSearch);
    }, [searchOutput]);

    const getVisibleResultItems = () => searchOutput.result.ResultItems.filter(isStandardResult);

    const getClientSidePaginatedSearchResults = React.useCallback(() => {
        const { pageNumber, pageSize } = queryInputState.value;
        const startIndex = (pageNumber - 1) * pageSize;
        const endIndex = pageNumber * pageSize;
        const visibleResults = getVisibleResultItems();
        const paginatedResults = visibleResults.slice(startIndex, endIndex);

        return SearchInterface.SearchQueryResponse.create({
            result: {
                ...searchOutput.result,
                ResultItems: paginatedResults,
                // Same source as pagesCount, so the displayed total and the page count always agree
                TotalNumberOfResults: visibleResults.length
            },
            isLegalOnlyAuthorized: searchOutput.isLegalOnlyAuthorized
        });
    }, [queryInputState.value, searchOutput]);

    const constructGlobalSearchQueryInput = React.useCallback((
        queryText: string
    ): SearchInterface.SearchQueryInput => {
        // Create a global search query without instanceId
        return Builder<SearchInterface.SearchQueryInput>()
            .queryText(queryText)
            .pageSize(INITIAL_PAGE_SIZE)
            .pageNumber(INITIAL_PAGE_NUMBER)
            .build();
    }, []);

    const [isQueryInitialized, setIsQueryInitialized] = React.useState(false);
    const lastSearchQueryRef = React.useRef<string>("");

    const createQuerySignature = React.useCallback((query: SearchInterface.SearchQueryInput): string => {
        return JSON.stringify({
            text: query.queryText,
            filters: query.filterConfiguration || [],
            page: query.pageNumber,
            sort: query.sortingConfiguration || [],
            legalOnly: query.showStrictlyLegalOnly
        });
    }, []);

    const executeSearchImmediate = React.useCallback((query: SearchInterface.SearchQueryInput) => {
        const querySignature = createQuerySignature(query);
        if (querySignature === lastSearchQueryRef.current) {
            return;
        }
        lastSearchQueryRef.current = querySignature;
        const isCurrent = searchRequest.begin();
        setSearchState({ status: "loading" });
        callApi(context.getSearchAPI().search, query).then(result => {
            if (!isCurrent()) return;
            setSearchState(result.ok
                ? { status: "loaded", data: result.data }
                : { status: "error", err: result.err });
        });
        // Fire-and-forget, like the live page
        callApi(
            context.getCommonAPI().recordViewHistory,
            IMetrics.RecordViewHistoryInput.create({
                url: `/search?queryText=${queryText}`,
                entityId: "global",
                type: "Search" // Use existing Type - TODO change this later
            })
        );
    }, [createQuerySignature, context, searchRequest, queryText]);

    React.useEffect(() => {
        queryInputState.setValue(constructGlobalSearchQueryInput(queryText));
        setAuthors([]);
        setPageLibs([]);
        setIsQueryInitialized(true);
    }, [queryText, constructGlobalSearchQueryInput]);

    React.useEffect(() => {
        if (!isQueryInitialized || !queryInputState.value?.queryText?.length) {
            return;
        }

        executeSearchImmediate(queryInputState.value);
    }, [queryInputState.value, isQueryInitialized, executeSearchImmediate]);

    React.useEffect(() => {
        if (searchState.status !== "loaded" || !searchOutput) return;

        if (authors.length === 0) {
            initAuthorOptions();
        }
        if (pageLibs.length === 0) {
            initPageLibraries();
        }
        if (availableInstances.length === 0) {
            initAvailableInstances();
        }
        setHasLegalOnlyAccess(searchOutput.isLegalOnlyAuthorized);
    }, [
        searchState.status,
        searchOutput,
        authors.length,
        pageLibs.length,
        availableInstances.length,
        initAuthorOptions,
        initPageLibraries,
        initAvailableInstances
    ]);

    // Publish the current search (term, filters; not sort or page) for the Quick chat panel.
    // Filter lines carry raw ids on purpose: the chat harness uses them as MCP tool arguments.
    const { setContext: setSearchChatContext, clearContext: clearSearchChatContext } = useSearchChatContext();
    const activeQuery = queryInputState.value;
    const filterKey = activeQuery ? getSearchFilterKey(activeQuery) : "";
    const publishedSearchChatContext = React.useRef<SearchChatContext | undefined>(undefined);
    React.useEffect(() => {
        if (!activeQuery?.queryText) return;
        publishedSearchChatContext.current = {
            queryText: activeQuery.queryText,
            filters: describeSearchFilters(activeQuery),
            filterKey
        };
        setSearchChatContext(publishedSearchChatContext.current);
    }, [activeQuery?.queryText, filterKey]);
    React.useEffect(() => () => clearSearchChatContext(publishedSearchChatContext.current), []);

    // Validation check after all hooks have been called
    if (!queryText?.trim()) {
        return (
            <div className="top-margin-5p">
                <Alert
                    type="error"
                    header="Cannot search for an empty query"
                >
                    <TextContent>
                        Please provide a valid search query and try again.
                    </TextContent>
                </Alert>
            </div>
        );
    }

    // Back to the page's starting filters; the chosen sort and page size are kept
    const clearFilters = () => {
        const current = queryInputState.value;
        queryInputState.setValue(
            Builder<SearchInterface.SearchQueryInput>(constructGlobalSearchQueryInput(current.queryText))
                .sortingConfiguration(current.sortingConfiguration)
                .pageSize(current.pageSize)
                .build()
        );
    };

    return (
        <React.Fragment>
            {queryInputState.value &&
                <div className="pf-search-page">
                    <SmartSearchFilterViewV2
                        preferences={searchPreferences}
                        queryInputState={queryInputState}
                        authors={authors}
                        pageLibraries={pageLibs}
                        availableInstances={availableInstances}
                        hasLegalOnlyAccess={hasLegalOnlyAccess}
                        isSearchRunning={searchState.status === "loading"}
                        isGlobalSearch={true}
                        defaultFileTypes={[]}
                        onClearFilters={clearFilters}
                    />
                    <div className="pf-search-results">
                        {searchState.status === "loading" &&
                            <div className="vertical-center top-margin-5p">
                                <LoadingSpinner msg={`Searching for ${queryInputState.value.queryText} ...`}/>
                            </div>
                        }
                        {searchState.status === "error" && (
                            <ErrorFlashbar error={searchError}/>
                        )}
                        {searchState.status === "loaded" && !!searchOutput && (
                            <React.Fragment>
                                <h4>
                                    Global Search Results for <em>"{queryInputState.value.queryText}"</em>
                                </h4>
                                {!!searchOutput.result.SpellCorrectedQueries?.length &&
                                    <div className="search-result-spell-correction">
                                        {searchOutput.result.SpellCorrectedQueries.map(spellCorrectionQuery => {
                                            const suggested = spellCorrectionQuery.SuggestedQueryText ?? "";
                                            const suggestedHref = `#/search?queryText=${encodeURIComponent(suggested)}`;
                                            return (
                                                <div key={suggested}>
                                                    Do you mean "<a href={suggestedHref} onClick={event => {
                                                        event.preventDefault();
                                                        queryInputState.setValue(
                                                            constructGlobalSearchQueryInput(suggested)
                                                        );
                                                        // Update URL without triggering page reload
                                                        navigate(`/search?queryText=${encodeURIComponent(suggested)}`, { replace: true });
                                                    }}>{suggested}</a>"?
                                                </div>
                                            );
                                        })}
                                        <br />
                                    </div>
                                }
                                <SearchResultPanelV2
                                    results={getClientSidePaginatedSearchResults()}
                                    queryInputState={queryInputState}
                                />
                                <nav className="centered-pagination" aria-label="pagination">
                                    <Pagination
                                        ariaLabels={{
                                            nextPageLabel: "Next page",
                                            previousPageLabel: "Previous page",
                                            pageLabel: pageNumber =>
                                                `Page ${pageNumber} of all pages`
                                        }}
                                        currentPageIndex={queryInputState.value.pageNumber}
                                        onChange={({ detail }) =>
                                            queryInputState.setValue(
                                                Builder<SearchInterface.SearchQueryInput>(queryInputState.value)
                                                    .pageNumber(detail.currentPageIndex)
                                                    .build()
                                            )
                                        }
                                        pagesCount={Math.ceil(
                                            getVisibleResultItems().length / queryInputState.value.pageSize
                                        )}
                                    />
                                </nav>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            }
        </React.Fragment>
    );
};
